import { LogPulseLogo } from './LogPulseLogo';

/**
 * The pulse from the demo film, borrowed as hero wallpaper: a mark inside a
 * ring, with rings travelling outward and fading. Same idea, different job —
 * in the film it carries a beat, here it only has to make the hero breathe.
 *
 * Deliberate choices:
 * - Desktop only (`hidden lg:block`). Below `lg` the hero stacks and there is
 *   no dead space to put it in; it would sit under the copy instead of beside
 *   it.
 * - The core sits low and right, half past the bottom edge of the section, so
 *   rings rise up behind the demo card and leave the frame on three sides.
 *   A ring you can see the whole of reads as a graphic; one that runs off the
 *   canvas reads as space. The section clips it (`overflow-hidden`).
 * - Five rings on a 16s cycle, ~3s apart, so two or three are always in
 *   flight — one lonely ring reads as a drawn circle, not a pulse.
 * - Alpha stays under 0.2. It is wallpaper: if you notice it while reading the
 *   headline, it is too strong.
 * - `prefers-reduced-motion` keeps the rings, drops the travel.
 */
export function HeroPulse() {
  return (
    <div
      aria-hidden="true"
      className="hidden lg:block pointer-events-none absolute z-0 select-none"
      style={{
        // Anchored to the bottom-right of the hero and pushed past both edges:
        // roughly a third of the ring system is outside the section. The offsets
        // are tuned so the core lands in the gap under the demo card rather than
        // behind it — the card is opaque, and a source you cannot see turns the
        // rings back into loose arcs.
        bottom: -346,
        right: -238,
        width: 900,
        height: 900,
      }}
    >
      <style>{`
        /* Opacity never reaches 1 and peaks late. Rings used to hit full
           strength at 10%, while still small: a 1px border wrapped tight
           around the core, stacked on its glow, which read as a hard circle
           flashing every few seconds. They now fade in over a quarter of the
           cycle, by which point the ring is wide enough for the stroke to
           dissolve into the background. */
        @keyframes heroPulseRing {
          0%   { transform: scale(0.34); opacity: 0; }
          26%  { opacity: 0.7; }
          100% { transform: scale(1); opacity: 0; }
        }
        @keyframes heroPulseCore {
          0%, 100% { transform: scale(1); opacity: 0.75; }
          50%      { transform: scale(1.06); opacity: 1; }
        }
        .hero-pulse-ring {
          position: absolute;
          inset: 0;
          border-radius: 9999px;
          border: 1px solid rgb(59 156 147 / 0.14);
          /* A hairline at exactly 1px lands on a pixel boundary and renders
             razor-sharp; a touch of blur keeps it wallpaper. */
          filter: blur(0.4px);
          /* The backwards fill mode matters more than it looks: four of the
             five rings wait out an animation-delay, and without a fill mode
             they sit in their unanimated state until their turn comes, at full
             size and opacity 1. Every page load painted four hard circles that
             only vanished once the animation took over. */
          animation: heroPulseRing 16s cubic-bezier(0.2, 0.55, 0.3, 1) infinite backwards;
        }
        :is(.dark) .hero-pulse-ring { border-color: rgb(95 189 181 / 0.13); }
        .hero-pulse-core { animation: heroPulseCore 5.5s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .hero-pulse-ring { animation: none; opacity: 0.6; }
          .hero-pulse-ring:nth-of-type(1) { transform: scale(0.3); }
          .hero-pulse-ring:nth-of-type(2) { transform: scale(0.48); }
          .hero-pulse-ring:nth-of-type(3) { transform: scale(0.66); }
          .hero-pulse-ring:nth-of-type(4) { transform: scale(0.84); }
          .hero-pulse-ring:nth-of-type(5) { transform: scale(1); }
          .hero-pulse-core { animation: none; }
        }
      `}</style>

      <div className="hero-pulse-ring" />
      <div className="hero-pulse-ring" style={{ animationDelay: '3.2s' }} />
      <div className="hero-pulse-ring" style={{ animationDelay: '6.4s' }} />
      <div className="hero-pulse-ring" style={{ animationDelay: '9.6s' }} />
      <div className="hero-pulse-ring" style={{ animationDelay: '12.8s' }} />

      {/* Glow behind the core so the centre reads as the source of the rings */}
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background:
            'radial-gradient(circle, rgb(59 156 147 / 0.09) 0%, rgb(59 156 147 / 0.035) 30%, transparent 58%)',
        }}
      />

      {/* Core: the mark in its ring, at wallpaper strength.
          Centring and breathing live on separate elements on purpose — both
          want `transform`, and the animation wins, which pushed the core half
          its own width off the rings' centre. */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <div className="hero-pulse-core flex h-28 w-28 items-center justify-center rounded-full border border-primary-500/20 bg-primary-500/[0.05]">
          <LogPulseLogo className="h-12 w-12 opacity-30" />
        </div>
      </div>
    </div>
  );
}
